import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Popover,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import {
    WEEKDAYS,
    addMonths,
    dayCards,
    dayLabel,
    insideFilter,
    monthGrid,
    monthKey,
    monthLabel,
    monthName,
    postingState,
    rowsByDay,
    shortDay,
} from './calendarUi';
import { platformLabel, useTaskPlatforms } from './taskFields';

// Cards a day box shows before "+N more"
const PER_DAY = 3;

const doneLabel = (row, isPublic) =>
    row.status === 'COMPLETED' ? 'Done' : row.stageName || (isPublic ? '' : 'No stage');

/** The stage in words, never colour alone; Done gets a tick, a late stage an icon. */
const StageLine = ({ row, isPublic, showDate }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
        <Typography variant="caption" color="text.secondary" component="span">
            {row.languageLabel}
            {showDate && row.date ? ` · ${shortDay(row.date)}` : ''}
        </Typography>
        {(row.upNext || doneLabel(row, isPublic)) && (
            <Chip
                size="small"
                variant="outlined"
                icon={row.status === 'COMPLETED' ? <CheckIcon /> : undefined}
                label={row.upNext ? `Up next: ${row.stageName}` : doneLabel(row, isPublic)}
                sx={{ height: 20, fontSize: '0.7rem' }}
            />
        )}
        {row.overdue && (
            <WarningAmberIcon fontSize="inherit" color="error" titleAccess="Stage is late" sx={{ fontSize: 16 }} />
        )}
    </Box>
);

/** The Posting calendar's line: where it goes out, the language when there's one, and its state in words. */
const PostingLine = ({ card, today }) => {
    const platforms = useTaskPlatforms();
    const first = card.rows[0];
    const state = postingState(card.rows, today);
    const place = [platformLabel(platforms, first.platform), card.grouped ? null : first.languageLabel].filter(Boolean).join(' · ');
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
            <Typography variant="caption" color="text.secondary" component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                {place}
            </Typography>
            <Chip
                size="small"
                variant="outlined"
                color={state.kind === 'notReady' ? 'warning' : 'default'}
                icon={state.kind === 'posted' ? <CheckIcon /> : state.kind === 'notReady' ? <WarningAmberIcon /> : undefined}
                label={state.label}
                sx={{ height: 20, fontSize: '0.7rem' }}
            />
        </Box>
    );
};

/**
 * One card on the calendar. posting: the Posting calendar's card. There a grouped post opens straight
 * away, because the drawer's strip shows its languages; a video has no strip, so it lists them first.
 * AV's rows open straight away too, as the Posting panel lists that day's languages.
 */
export const TaskCard = ({ card, onOpen, onOpenGroup, isPublic, compact, showDate, tabIndex, posting, today }) => {
    const first = card.rows[0];
    const done = card.rows.every((r) => r.status === 'COMPLETED');
    const late = card.rows.some((r) => r.overdue);
    // A grouped card opens its languages even on a link, where single tasks don't open
    const opensGroup = card.grouped && !(posting && (first.contentType === 'POST' || first.word));
    const clickable = opensGroup ? !!onOpenGroup : !!onOpen;
    const open = (e) => (opensGroup ? onOpenGroup(e.currentTarget, card) : onOpen(first));
    return (
        <Card
            variant="outlined"
            component={clickable ? 'button' : 'div'}
            type={clickable ? 'button' : undefined}
            onClick={clickable ? open : undefined}
            tabIndex={clickable ? tabIndex : undefined}
            sx={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                font: 'inherit',
                color: 'inherit',
                // Done is greyed by colour, not opacity, so its text keeps its contrast
                backgroundColor: done ? 'grey.100' : 'background.paper',
                p: compact ? 0.5 : 1,
                mb: 0.5,
                cursor: clickable ? 'pointer' : 'default',
                ...(first.upNext && { border: '1.5px dashed', borderColor: 'text.secondary' }),
                '&:hover': clickable ? { borderColor: 'primary.main' } : undefined,
                '&:focus-visible': { outline: '3px solid', outlineColor: 'secondary.main', outlineOffset: 1 },
            }}
        >
            <Typography
                variant="caption"
                component="div"
                sx={{
                    fontWeight: 600,
                    lineHeight: 1.3,
                    wordBreak: 'break-word',
                    color: done ? 'text.secondary' : 'text.primary',
                }}
            >
                {done && <CheckIcon sx={{ fontSize: 13, verticalAlign: '-2px', mr: 0.25 }} />}
                {first.title}
                {card.grouped ? ` · ${card.rows.length} languages` : ''}
            </Typography>
            {posting ? (
                <PostingLine card={card} today={today} />
            ) : card.grouped ? (
                late && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <WarningAmberIcon color="error" sx={{ fontSize: 16 }} />
                        <Typography variant="caption" color="error">
                            {card.rows.filter((r) => r.overdue).length} late
                        </Typography>
                    </Box>
                )
            ) : (
                <StageLine row={first} isPublic={isPublic} showDate={showDate} />
            )}
        </Card>
    );
};

/** A tray beside the grid: Overdue or No date. Lists what the server sent, with the full count. */
const Tray = ({ id, title, tray, onOpen, isPublic, emptyText, posting, today }) => {
    const [all, setAll] = useState(false);
    const rows = tray?.rows || [];
    const shown = all ? rows : rows.slice(0, 8);
    return (
        <Box
            component="section"
            id={id}
            tabIndex={-1}
            aria-label={`${title}, ${tray?.count || 0}`}
            sx={{ mb: 2, outline: 'none' }}
        >
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {title} ({tray?.count || 0})
            </Typography>
            {rows.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                    {emptyText}
                </Typography>
            )}
            {shown.map((r, i) => (
                <TaskCard
                    key={`${r.id}:${i}`}
                    card={{ key: r.id, rows: [r], grouped: false }}
                    onOpen={onOpen}
                    isPublic={isPublic}
                    compact
                    showDate
                    posting={posting}
                    today={today}
                />
            ))}
            {rows.length > shown.length && (
                <Button size="small" onClick={() => setAll(true)}>
                    Show all {rows.length}
                </Button>
            )}
            {tray?.count > rows.length && (
                <Typography variant="caption" color="text.secondary" component="div">
                    Showing {rows.length} of {tray.count}. Narrow the filters to see the rest.
                </Typography>
            )}
        </Box>
    );
};

/**
 * The calendar layout (rev6 Part 4, 6f): Month or a List of days, with the Overdue and No date
 * trays beside it. Phones always get the list. Dates here have no times, so there is no week
 * grid. Nothing is moved on the calendar (D36): dates change in the drawer.
 *
 * data: the server's calendar result. group: languages of one file on one day share a card
 * (All tasks and links). onOpen: opens a task; none on a no-login link. on/onOnChange: the Place
 * by switch; none on a link. posting: the Posting calendar's Calendar tab, month only, with its own
 * cards and no Overdue tray.
 */
const TaskCalendar = ({
    data,
    month,
    onMonthChange,
    mode,
    onModeChange,
    on,
    onOnChange,
    group,
    groupKey,
    onOpen,
    isPublic = false,
    dateFilterLabel,
    posting = false,
}) => {
    const theme = useTheme();
    const phone = useMediaQuery(theme.breakpoints.down('sm'));
    const wide = useMediaQuery(theme.breakpoints.up('lg'));
    const layout = phone ? 'list' : posting ? 'month' : mode;
    const cardProps = { posting, today: data?.today };
    const [groupOpen, setGroupOpen] = useState(null);
    const [dayOpen, setDayOpen] = useState(null);
    useTrackUsage('CalendarDayDialog', 'dialog', 'tasks', !!dayOpen);
    const [focusDay, setFocusDay] = useState(null);
    const gridRef = useRef(null);
    const focusAfterMove = useRef(false);

    const today = data?.today;
    const byDay = rowsByDay(data?.rows);
    const weeks = monthGrid(month);
    const inMonth = (iso) => monthKey(iso) === month;
    const monthHasRows = (data?.rows || []).some((r) => inMonth(r.date));
    // The data is for this month's grid, not still the last month's while the next one loads
    const fresh = !!data && data.from === weeks[0][0];
    const shade = (iso) => !insideFilter(iso, data?.filterFrom, data?.filterTo);
    const cards = (iso) => dayCards(byDay[iso] || [], group, groupKey);
    const openGroup = (anchor, card) => setGroupOpen({ anchor, card });

    const tabDay =
        focusDay && weeks.flat().includes(focusDay) ? focusDay : weeks.flat().find((d) => d === today) || `${month}-01`;

    // After Page Up or Page Down the grid is new: put focus back on its focusable day
    useEffect(() => {
        if (!focusAfterMove.current) return;
        focusAfterMove.current = false;
        gridRef.current?.querySelector('td[tabindex="0"]')?.focus();
    }, [month]);

    const moveFocus = (iso, e) => {
        if (e.target !== e.currentTarget) return; // keys inside a card belong to the card
        if (e.key === 'PageUp' || e.key === 'PageDown') {
            e.preventDefault();
            focusAfterMove.current = true;
            setFocusDay(null);
            onMonthChange(addMonths(month, e.key === 'PageUp' ? -1 : 1));
            return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const first = e.currentTarget.querySelector('button');
            if (first) first.focus();
            return;
        }
        const steps = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
        if (!(e.key in steps)) return;
        e.preventDefault();
        const flat = weeks.flat();
        const next = flat[flat.indexOf(iso) + steps[e.key]];
        if (!next) return;
        setFocusDay(next);
        gridRef.current?.querySelector(`[data-day="${next}"]`)?.focus();
    };
    const iconSx = phone ? { minWidth: 44, minHeight: 44 } : undefined;

    const bar = (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <IconButton
                size="small"
                sx={iconSx}
                aria-label="Previous month"
                onClick={() => onMonthChange(addMonths(month, -1))}
            >
                <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6" component="h2" sx={{ minWidth: 170, textAlign: 'center' }} aria-live="polite">
                {monthLabel(month)}
            </Typography>
            <IconButton
                size="small"
                sx={iconSx}
                aria-label="Next month"
                onClick={() => onMonthChange(addMonths(month, 1))}
            >
                <ChevronRightIcon />
            </IconButton>
            <Button size="small" variant="outlined" onClick={() => today && onMonthChange(monthKey(today))}>
                Today
            </Button>
            <Box sx={{ flex: 1 }} />
            {onOnChange && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" id="place-by">
                        Place by
                    </Typography>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={on}
                        onChange={(_, v) => v && onOnChange(v)}
                        aria-labelledby="place-by"
                    >
                        <ToggleButton value="posting">Posting date</ToggleButton>
                        <ToggleButton value="due">Due date</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}
            {!phone && !posting && (
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={mode}
                    onChange={(_, v) => v && onModeChange(v)}
                    aria-label="Calendar layout"
                >
                    <ToggleButton value="month">Month</ToggleButton>
                    <ToggleButton value="list">List</ToggleButton>
                </ToggleButtonGroup>
            )}
        </Box>
    );

    const notes = (
        <>
            {dateFilterLabel && (data?.filterFrom || data?.filterTo) && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Showing {dateFilterLabel} only. Striped days are outside it.
                </Typography>
            )}
            {!wide && (
                <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    {[
                        ['tray-overdue', 'Overdue', data?.overdue],
                        ['tray-nodate', 'No date', data?.noDate],
                    ].filter(([id]) => !posting || id === 'tray-nodate').map(([id, title, tray]) => (
                        <Button
                            key={id}
                            size="small"
                            variant="outlined"
                            color={id === 'tray-overdue' && tray?.count ? 'error' : 'inherit'}
                            onClick={() => {
                                const el = document.getElementById(id);
                                el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                el?.focus({ preventScroll: true });
                            }}
                        >
                            {title} ({tray?.count || 0})
                        </Button>
                    ))}
                </Box>
            )}
            {data?.truncated && (
                <Alert severity="info" sx={{ mb: 1 }}>
                    There are more tasks than the calendar can show. Narrow the filters to see them all.
                </Alert>
            )}
        </>
    );

    const emptyMonth = fresh && !monthHasRows && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="subtitle1">Nothing in {monthName(month)}</Typography>
            {data.nextDate && (
                <Button sx={{ mt: 1 }} onClick={() => onMonthChange(monthKey(data.nextDate))}>
                    Go to next: {dayLabel(data.nextDate)}
                </Button>
            )}
        </Box>
    );

    const grid = (
        <Box sx={{ overflowX: 'auto' }}>
            <Box
                component="table"
                role="grid"
                ref={gridRef}
                aria-label={`${monthLabel(month)} calendar`}
                sx={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 640 }}
            >
                <thead>
                    <tr>
                        {WEEKDAYS.map((d) => (
                            <Box
                                component="th"
                                key={d}
                                scope="col"
                                sx={{
                                    typography: 'caption',
                                    color: 'text.secondary',
                                    py: 0.5,
                                    textAlign: 'left',
                                    px: 0.75,
                                }}
                            >
                                {d}
                            </Box>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {weeks.map((week) => (
                        <tr key={week[0]}>
                            {week.map((iso) => {
                                const dayCardsList = cards(iso);
                                const count = (byDay[iso] || []).length;
                                const isToday = iso === today;
                                const outside = shade(iso);
                                const tabbable = iso === tabDay ? 0 : -1;
                                return (
                                    <Box
                                        component="td"
                                        role="gridcell"
                                        key={iso}
                                        data-day={iso}
                                        tabIndex={tabbable}
                                        onKeyDown={(e) => moveFocus(iso, e)}
                                        onFocus={() => setFocusDay(iso)}
                                        aria-label={`${dayLabel(iso)}, ${count} task${count === 1 ? '' : 's'}${isToday ? ', today' : ''}${
                                            outside ? ', outside the filter' : ''
                                        }`}
                                        sx={{
                                            verticalAlign: 'top',
                                            height: 116,
                                            p: 0.5,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            outline: isToday ? '2px solid' : 'none',
                                            outlineColor: 'primary.main',
                                            outlineOffset: -2,
                                            // Stripes, not a faint tint, so days outside the filter are plain to see
                                            backgroundImage: outside
                                                ? `repeating-linear-gradient(135deg, transparent 0 6px, ${theme.palette.action.selected} 6px 8px)`
                                                : 'none',
                                            '&:focus-visible': {
                                                outline: '3px solid',
                                                outlineColor: 'secondary.main',
                                                outlineOffset: -3,
                                            },
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            component="div"
                                            sx={{
                                                fontWeight: isToday ? 700 : 400,
                                                color: inMonth(iso) ? 'text.primary' : 'text.disabled',
                                                mb: 0.25,
                                            }}
                                        >
                                            {Number(iso.slice(8))}
                                        </Typography>
                                        {dayCardsList.slice(0, PER_DAY).map((card) => (
                                            <TaskCard
                                                key={card.key}
                                                card={card}
                                                onOpen={onOpen}
                                                onOpenGroup={openGroup}
                                                isPublic={isPublic}
                                                compact
                                                tabIndex={tabbable}
                                                {...cardProps}
                                            />
                                        ))}
                                        {dayCardsList.length > PER_DAY && (
                                            <Button
                                                size="small"
                                                tabIndex={tabbable}
                                                sx={{ minWidth: 0, p: 0, fontSize: '0.72rem' }}
                                                onClick={() => setDayOpen(iso)}
                                            >
                                                +{dayCardsList.length - PER_DAY} more
                                            </Button>
                                        )}
                                    </Box>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </Box>
        </Box>
    );

    const days = Object.keys(byDay).filter(inMonth).sort();
    const list = (
        <Box>
            {days.map((iso) => (
                <Box key={iso} component="section" aria-label={dayLabel(iso, true)} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" component="h3" sx={{ mb: 0.5 }}>
                        {dayLabel(iso, true)}
                        {iso === today ? ' · today' : ''}
                        {shade(iso) && (
                            <Typography component="span" variant="caption" color="text.secondary">
                                {' '}
                                (outside the filter)
                            </Typography>
                        )}
                    </Typography>
                    {cards(iso).map((card) => (
                        <TaskCard
                            key={card.key}
                            card={card}
                            onOpen={onOpen}
                            onOpenGroup={openGroup}
                            isPublic={isPublic}
                            {...cardProps}
                        />
                    ))}
                </Box>
            ))}
        </Box>
    );

    const trays = (
        <Box sx={{ width: wide ? 260 : '100%', flexShrink: 0, ml: wide ? 2 : 0, mt: wide ? 0 : 2 }}>
            {!posting && (
                <Tray
                    id="tray-overdue"
                    title="Overdue"
                    tray={data?.overdue}
                    onOpen={onOpen}
                    isPublic={isPublic}
                    emptyText="Nothing overdue."
                />
            )}
            <Tray
                id="tray-nodate"
                title="No date"
                tray={data?.noDate}
                onOpen={onOpen}
                isPublic={isPublic}
                emptyText="Every task has a date."
                {...cardProps}
            />
        </Box>
    );

    return (
        <Box>
            {bar}
            {notes}
            <Box sx={{ display: 'flex', flexDirection: wide ? 'row' : 'column', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    {layout === 'month' && grid}
                    {layout === 'list' && (monthHasRows ? list : emptyMonth)}
                    {layout === 'month' && emptyMonth}
                </Box>
                {trays}
            </Box>

            <Popover
                open={!!groupOpen}
                anchorEl={groupOpen?.anchor}
                onClose={() => setGroupOpen(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                {groupOpen && (
                    <Box sx={{ p: 1.5, width: 280 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {groupOpen.card.rows[0].title} · {shortDay(groupOpen.card.rows[0].date)}
                        </Typography>
                        {groupOpen.card.rows.map((r) => (
                            <TaskCard
                                key={`${r.id}:${r.slot || 1}`}
                                card={{ key: r.id, rows: [r], grouped: false }}
                                onOpen={
                                    onOpen
                                        ? (row) => {
                                              setGroupOpen(null);
                                              onOpen(row);
                                          }
                                        : undefined
                                }
                                isPublic={isPublic}
                                compact
                                {...cardProps}
                            />
                        ))}
                    </Box>
                )}
            </Popover>

            <Dialog open={!!dayOpen} onClose={() => setDayOpen(null)} maxWidth="xs" fullWidth>
                <DialogTitle>{dayOpen ? dayLabel(dayOpen, true) : ''}</DialogTitle>
                <DialogContent>
                    {dayOpen &&
                        cards(dayOpen).map((card) => (
                            <TaskCard
                                key={card.key}
                                card={card}
                                onOpen={
                                    onOpen
                                        ? (row) => {
                                              setDayOpen(null);
                                              onOpen(row);
                                          }
                                        : undefined
                                }
                                onOpenGroup={openGroup}
                                isPublic={isPublic}
                                {...cardProps}
                            />
                        ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDayOpen(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TaskCalendar;
